import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
} from '@angular/core';
import {v4 as uuidv4} from 'uuid';

@Component({
    selector: 'ui-modal',
    templateUrl: './ui-modal.component.html',
    styleUrls: ['./ui-modal.component.scss'],
    standalone: false
})
export class UiModalComponent implements OnInit, AfterViewInit, OnDestroy {
    private _isModalAppendedToBody = false;
    private _isModalOpened = false;

    get minContentWidth() {
        return 248;
    }

    get minContentHeight() {
        return 228 - (this.modalHeaderContent?.nativeElement?.offsetHeight ?? 0);
    }

    get contentWidth() {
        const min = this.minContentWidth;
        let intValue: number;
        const max = this.maxContentWidth;

        if (typeof this.width == 'string') {
            if (!/^\d+(%|vw|vh|px)$/.test(this.width)) {
                return this.width;
            }

            intValue = this._convertToAbsolute(window.innerWidth, this.width);
        }

        if (intValue > max) {
            intValue = max;
        }

        if (intValue < min) {
            intValue = min;
        }

        return intValue + 'px';
    }

    get contentHeight() {
        const min = this.minContentHeight;
        let intValue: number;
        const max = this.maxContentHeight;

        if (typeof this.height == 'string') {
            if (!/^\d+(%|vw|vh|px)$/.test(this.height)) {
                return this.height;
            }

            intValue = this._convertToAbsolute(window.innerHeight, this.height);
        }

        if (intValue > max) {
            intValue = max;
        }

        if (intValue < min) {
            intValue = min;
        }

        return intValue + 'px';
    }

    get maxContentWidth() {
        return window.innerWidth - 280;
    }

    get maxContentHeight() {
        return window.innerHeight - 64 - (this.modalHeaderContent?.nativeElement?.offsetHeight ?? 0);
    }

    @Input() id: string;
    @Input() width: string | number = 'auto';
    @Input() height: string | number = 'auto';

    @Output('opened') openedEmitter = new EventEmitter<any>();
    @Output('closed') closedEmitter = new EventEmitter<any>();

    @ViewChild('modal') modal: ElementRef;
    @ContentChild('modalHeader') modalHeaderContent: ElementRef;
    @ContentChild('modalIcon') modalIconContent: ElementRef;
    @ContentChild('modalTitle') modalTitleContent: ElementRef;
    @ContentChild('modalSubtitle') modalSubtitleContent: ElementRef;
    @ViewChild('modalContent') modalContent: ElementRef;

    get isModalOpened(): boolean {
        return this._isModalOpened;
    }

    set isModalOpened(value: boolean) {
        if (value === this._isModalOpened) return;

        if (value) {
            if (!this._isModalAppendedToBody) {
                this.renderer.appendChild(document.body, this.modal?.nativeElement);
                this._isModalAppendedToBody = true;
            }

            this.openedEmitter.emit(value);
        } else {
            this.closedEmitter.emit(value);
        }

        this._isModalOpened = value;
    }

    constructor(
        private renderer: Renderer2) {
    }

    ngOnInit(): void {
        if (!this.id) {
            this.id = uuidv4();
        }
    }

    ngAfterViewInit() {
        const modalEl = this.modal?.nativeElement;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach(() => {
                this.isModalOpened = modalEl.classList.contains(`opened`);
            });
        });

        observer.observe(modalEl, {attributes: true, attributeFilter: ['class']});
    }

    openModal() {
        this.isModalOpened = true;
    }

    showModal() {
        this.openModal();
    }

    closeModal() {
        this.modal?.nativeElement?.classList.remove('opened');
        this.isModalOpened = false;
    }

    ngOnDestroy() {
        this.renderer.removeChild(document.body, this.modal?.nativeElement);
        this._isModalAppendedToBody = false;
    }

    _convertToAbsolute(relativeInt: number, value: string) {
        let intValue = parseInt((value.replaceAll('\D', '')));

        if (!value.endsWith('px')) {
            intValue = intValue/100;

            intValue = relativeInt * intValue;
        }

        return intValue;
    }
}
